import { useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { confirmDialog } from "primereact/confirmdialog";

import { currencyFormat } from "@shared/utility/formats/currency-format";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import {
  useQyGetDiscount,
  useQyCreateDiscount,
  useQyDeleteDiscount,
} from "@factory/query/discount.query";
import { QueryKeyBilling } from "@factory/model/query-model-billing";
import { QueryKeyContract } from "@factory/model/query-model-contract";

export interface DiscountManagementDialogProps {
  visible: boolean;
  onHide: () => void;
  billing?: {
    code?: string;
    session?: number;
    original_amount?: number;
    amount?: number;
    total_discount?: number;
    net_amount?: number;
    amount_paid?: number;
    balance?: number;
  };
}

export function DiscountManagementDialog({
  visible,
  onHide,
  billing,
}: DiscountManagementDialogProps) {
  const { formatDateLocal } = useDateTimeContext();
  const { showError, showSuccess } = useNotificationContext();
  const { mapCreateDiscount, mapVoidDiscount } = useFormApiMapper();
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState("");

  const billingCode = billing?.code || "";
  const originalAmount = billing?.original_amount ?? billing?.amount ?? 0;
  const totalDiscount = billing?.total_discount ?? 0;
  const netAmount = billing?.net_amount ?? originalAmount - totalDiscount;
  const amountPaid = billing?.amount_paid ?? 0;
  const balance = billing?.balance ?? 0;

  const invalidateBillingAndContract = () => {
    queryClient.invalidateQueries({
      queryKey: [QueryKeyBilling],
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: [QueryKeyContract],
      exact: false,
    });
  };

  const { data: discountResponse, isLoading: isLoadingDiscounts } =
    useQyGetDiscount(
      "",
      99,
      0,
      { created_at: "DESC" },
      { billing: billingCode },
      visible && !!billingCode
    );
  const discounts = useMemo(
    () => discountResponse?.data || [],
    [discountResponse]
  );

  const handleCreateSuccess = () => {
    showSuccess("Discount applied");
    setAmount(null);
    setReason("");
    invalidateBillingAndContract();
  };
  const { mutate: createDiscount, isLoading: isCreating } =
    useQyCreateDiscount(handleCreateSuccess);

  const handleVoidSuccess = () => {
    showSuccess("Discount voided");
    invalidateBillingAndContract();
  };
  const { mutate: voidDiscount } = useQyDeleteDiscount(handleVoidSuccess);

  const handleAddDiscount = () => {
    if (!billingCode) return;
    if (!amount || amount <= 0) {
      showError("Enter a discount amount greater than zero.");
      return;
    }
    // Soft, client-side guard only - the backend re-validates against the
    // outstanding balance authoritatively at the time of the request, since
    // it can change concurrently.
    if (amount > balance) {
      showError(
        `Discount cannot exceed the outstanding balance of ${currencyFormat(balance, "PHP")}.`
      );
      return;
    }

    createDiscount(mapCreateDiscount(billingCode, amount, reason || undefined));
  };

  const handleVoidDiscount = (discountCode: string) => {
    let voidReason = "";
    confirmDialog({
      message: (
        <div className="flex flex-column gap-2 w-full">
          <span>
            Are you sure you want to void this discount? This cannot be
            undone.
          </span>
          <label
            htmlFor={`void-discount-reason-${discountCode}`}
            className="font-medium"
          >
            Reason (required)
          </label>
          <InputTextarea
            id={`void-discount-reason-${discountCode}`}
            rows={3}
            autoResize
            onChange={(e) => {
              voidReason = e.target.value;
            }}
            placeholder="Why is this discount being voided?"
          />
        </div>
      ),
      header: "Void Discount",
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => {
        if (!voidReason.trim()) {
          showError("A reason is required to void this discount.");
          return;
        }
        voidDiscount(mapVoidDiscount(discountCode, voidReason.trim()));
      },
    });
  };

  return (
    <Dialog
      header={`Discounts - Session ${billing?.session ?? ""}`}
      visible={visible}
      onHide={onHide}
      style={{ width: "40rem" }}
      modal
    >
      <div className="grid mb-4">
        <div className="col-6 md:col-4">
          <div className="text-500 text-sm">Original Amount</div>
          <div className="text-xl font-medium">
            {currencyFormat(originalAmount, "PHP")}
          </div>
        </div>
        <div className="col-6 md:col-4">
          <div className="text-500 text-sm">Total Discount</div>
          <div className="text-xl font-medium text-orange-600">
            {currencyFormat(totalDiscount, "PHP")}
          </div>
        </div>
        <div className="col-6 md:col-4">
          <div className="text-500 text-sm">Net Amount</div>
          <div className="text-xl font-medium">
            {currencyFormat(netAmount, "PHP")}
          </div>
        </div>
        <div className="col-6 md:col-4">
          <div className="text-500 text-sm">Paid</div>
          <div className="text-xl font-medium text-green-600">
            {currencyFormat(amountPaid, "PHP")}
          </div>
        </div>
        <div className="col-6 md:col-4">
          <div className="text-500 text-sm">Remaining Balance</div>
          <div className="text-xl font-medium">
            {currencyFormat(balance, "PHP")}
          </div>
        </div>
      </div>

      <DataTable
        value={discounts}
        loading={isLoadingDiscounts}
        emptyMessage="No discounts recorded for this session."
        className="mb-4"
      >
        <Column
          field="amount"
          header="Amount"
          body={(row) => currencyFormat(row.amount, "PHP")}
        />
        <Column field="reason" header="Reason" />
        <Column
          field="created_at"
          header="Date"
          body={(row) =>
            row.created_at ? formatDateLocal(row.created_at) : "-"
          }
        />
        <Column
          field="is_active"
          header="Status"
          body={(row) => (
            <Tag
              value={row.is_active ? "ACTIVE" : "VOIDED"}
              severity={row.is_active ? "success" : "danger"}
            />
          )}
        />
        <Column
          header="Action"
          body={(row) =>
            row.is_active ? (
              <Button
                label="Void"
                severity="danger"
                text
                onClick={() => handleVoidDiscount(row.code)}
              />
            ) : null
          }
        />
      </DataTable>

      <div className="flex flex-column gap-2 border-top-1 surface-border pt-3">
        <span className="font-medium">Add a discount</span>
        <div className="flex flex-wrap gap-2 align-items-start">
          <InputNumber
            value={amount}
            onValueChange={(e) => setAmount(e.value ?? null)}
            mode="currency"
            currency="PHP"
            locale="en-PH"
            placeholder="Amount"
            disabled={balance <= 0}
          />
          <InputTextarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
            rows={1}
            autoResize
            className="flex-1"
            disabled={balance <= 0}
          />
          <Button
            label="Add Discount"
            onClick={handleAddDiscount}
            loading={isCreating}
            disabled={balance <= 0}
          />
        </div>
        {balance <= 0 && (
          <small className="text-500">
            This session has no outstanding balance, so a discount cannot be
            applied.
          </small>
        )}
      </div>
    </Dialog>
  );
}

export default DiscountManagementDialog;
